import { useEffect, useMemo, useState } from 'react'
import { getAddress, isAddress, JsonRpcProvider } from 'ethers'
import type { SafeTransaction } from '@safe-global/types-kit'
import { useSafeShieldAnalyzeCounterpartyV1Mutation } from '@safe-global/store/gateway/AUTO_GENERATED/safe-shield'
import { useAddressBookCheck } from './address-analysis/address-book-check/useAddressBookCheck'
import { useAddressActivity } from './address-analysis/address-activity/useAddressActivity'
import { type RecipientAnalysisResults, type ContractAnalysisResults, StatusGroup } from '../types'
import type { AsyncResult } from '@safe-global/utils/hooks/useAsync'
import { filterNonSafeRecipients, mergeAnalysisResults } from '../utils'
import { ErrorType, getErrorInfo } from '../utils/errors'

/**
 * Hook for fetching and analyzing counterparty addresses (both recipients and contracts)
 * Performs backend API calls and local checks (ADDRESS_BOOK, RECIPIENT_ACTIVITY) for recipients
 * Returns separate results for recipients and contracts
 *
 * @param safeAddress - The Safe contract address
 * @param chainId - The chain ID where the Safe is deployed
 * @param safeTx - SafeTransaction object containing transaction data
 * @param isInAddressBook - Function to check if address is in address book
 * @param ownedSafes - Array of Safe addresses owned by the user
 * @param web3ReadOnly - Read-only web3 provider for on-chain checks
 * @returns Object containing recipient and contract analysis results with loading and error states
 */
export function useCounterpartyAnalysis({
  safeAddress,
  chainId,
  safeTx,
  isInAddressBook,
  ownedSafes = [],
  web3ReadOnly,
}: {
  safeAddress: string
  chainId: string
  safeTx?: SafeTransaction
  isInAddressBook: (address: string, chainId: string) => boolean
  ownedSafes: string[]
  web3ReadOnly?: JsonRpcProvider
}): {
  recipient: AsyncResult<RecipientAnalysisResults>
  contract: AsyncResult<ContractAnalysisResults>
} {
  const [triggerAnalysis, { data: counterpartyData, error, isLoading: isCounterpartyLoading }] =
    useSafeShieldAnalyzeCounterpartyV1Mutation()

  const [hasTriggered, setHasTriggered] = useState(false)

  // Extract transaction data from SafeTransaction
  const transactionData = useMemo(() => {
    if (safeTx?.data.to) {
      return {
        to: getAddress(safeTx.data.to),
        value: safeTx.data.value,
        data: safeTx.data.data,
        operation: safeTx.data.operation as 0 | 1,
      }
    }
  }, [safeTx?.data.to, safeTx?.data.value, safeTx?.data.data, safeTx?.data.operation])

  // Trigger the mutation when transaction data is available
  useEffect(() => {
    if (transactionData && !hasTriggered) {
      triggerAnalysis({
        chainId,
        safeAddress,
        counterpartyAnalysisRequestDto: transactionData,
      })
      setHasTriggered(true)
    }
  }, [transactionData, chainId, safeAddress, triggerAnalysis, hasTriggered])

  // Reset hasTriggered when transaction data changes
  useEffect(() => {
    setHasTriggered(false)
  }, [transactionData])

  const recipientAnalysisByAddress = useMemo(() => {
    if (!counterpartyData?.recipient) {
      return undefined
    }

    return Object.entries(counterpartyData.recipient).reduce<RecipientAnalysisResults>((acc, [address, result]) => {
      if (!address || !isAddress(address)) {
        return acc
      }

      const checksummedAddress = getAddress(address)
      acc[checksummedAddress] = result as RecipientAnalysisResults[string]
      return acc
    }, {})
  }, [counterpartyData])

  const recipientAddresses = useMemo(() => Object.keys(recipientAnalysisByAddress || {}), [recipientAnalysisByAddress])

  // Filter out recipient addresses that are Safe accounts
  const nonSafeRecipients = useMemo(
    () => filterNonSafeRecipients(recipientAnalysisByAddress),
    [recipientAnalysisByAddress],
  )

  // Perform local checks on recipient addresses
  const addressBookCheck = useAddressBookCheck(chainId, recipientAddresses, isInAddressBook, ownedSafes)
  const [activityCheck, activityCheckError, activityCheckLoading] = useAddressActivity(nonSafeRecipients, web3ReadOnly)

  const fetchError = useMemo(() => {
    if (error) {
      return new Error('error' in error ? error.error : 'Failed to fetch counterparty analysis')
    }
    return undefined
  }, [error])

  // Check if any of the checks are loading or if the results are not complete
  const isLoading = useMemo(
    () => isCounterpartyLoading || activityCheckLoading,
    [isCounterpartyLoading, activityCheckLoading],
  )

  // Merge backend recipient results with local checks
  const mergedRecipientResults = useMemo(() => {
    if (fetchError || activityCheckError) {
      return { [safeAddress]: { [StatusGroup.COMMON]: [getErrorInfo(ErrorType.RECIPIENT)] } }
    }

    // Only merge results if all of them are available
    if (isLoading) {
      return undefined
    }

    return recipientAnalysisByAddress
      ? mergeAnalysisResults(recipientAnalysisByAddress, addressBookCheck, activityCheck)
      : undefined
  }, [
    recipientAnalysisByAddress,
    addressBookCheck,
    activityCheck,
    fetchError,
    activityCheckError,
    isLoading,
    safeAddress,
  ])

  const contractResults = useMemo(() => {
    if (fetchError) {
      return {
        [safeAddress]: {
          name: '',
          logoUrl: '',
          [StatusGroup.COMMON]: [getErrorInfo(ErrorType.CONTRACT)],
        },
      }
    }
    if (!counterpartyData?.contract) {
      return undefined
    }

    return counterpartyData.contract as ContractAnalysisResults
  }, [counterpartyData?.contract, fetchError, safeAddress])

  // Return results in the expected format
  return {
    recipient: [mergedRecipientResults, fetchError || activityCheckError, isLoading],
    contract: [contractResults, fetchError, isCounterpartyLoading],
  }
}
